import { useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Plugs, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const NAMES = { smtp: "SMTP relay", sendgrid: "SendGrid", resend: "Resend" };

/** Live connection test of the mail transports (nothing is sent) — separates "port blocked", "bad password" and "sender not verified". */
export function EmailProbeCard() {
  const [probe, setProbe] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await API.get("/platform/email-health/probe");
      setProbe(data);
      toast[data.any_ok ? "success" : "error"](data.any_ok ? "Mail server reachable and login accepted" : "No mail transport is working from this server");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't run the connection test");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="email-probe-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-1.5"><Plugs size={16} /> Connection test</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Connects and logs in to each configured transport from this server without sending anything — run it here on the published app when invites stop arriving.</p>
        </div>
        <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="email-probe-run">{busy ? "Testing…" : "Test connection"}</Button>
      </div>
      {probe && (
        <ul className="mt-3 space-y-1.5" data-testid="email-probe-results">
          {probe.probes.map((p) => {
            const ok = p.ok && p.verified_sender !== false;
            return (
              <li key={p.transport} className="flex items-start gap-2 text-xs" data-testid={`email-probe-${p.transport}`} data-ok={ok}>
                {ok ? <CheckCircle size={14} className="text-emerald-600 mt-0.5 shrink-0" weight="fill" /> : <WarningCircle size={14} className="text-red-600 mt-0.5 shrink-0" weight="fill" />}
                <div className="min-w-0">
                  <span className="font-medium">{NAMES[p.transport] || p.transport}</span>
                  {p.host && <span className="text-muted-foreground"> · {p.host}:{p.port}</span>}
                  {p.ms != null && <span className="text-muted-foreground"> · {p.ms} ms</span>}
                  {ok && <Badge className="ml-2 text-[9px] bg-emerald-100 text-emerald-700">OK</Badge>}
                  {p.error && <div className="text-red-700 dark:text-red-300 break-words mt-0.5">{p.error}</div>}
                  {p.note && <div className="text-muted-foreground mt-0.5">{p.note}</div>}
                </div>
              </li>
            );
          })}
          {probe.probes.length === 0 && <li className="text-xs text-red-600">No transport configured.</li>}
        </ul>
      )}
    </div>
  );
}
